import { CardShell } from '@atlas/ui-components'
import { useState } from 'react'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { useMappingVersions, useRepublishVersion } from '@/hooks/useWorkfrontMappings'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import type { MappingVersionDTO } from '@/services/workfront-mappings.service'
import { VersionsTable } from './components/MappingTables'
import { useActorName, useReportLoadFailure } from './tab-support'

/**
 * History: every published version; re-publishing an older one rolls back to its mappings,
 * as a new version. A roll back re-maps every bundle, so it asks once more first.
 */
export function HistoryTab() {
	const query = useMappingVersions()
	const republish = useRepublishVersion()
	const nameOf = useActorName()
	const [rollingBackTo, setRollingBackTo] = useState<MappingVersionDTO | null>(null)
	const [failure, setFailure] = useState<string | null>(null)
	useReportLoadFailure(query.isError, 'versions')

	const close = () => {
		setRollingBackTo(null)
		setFailure(null)
	}
	const rollBack = (version: MappingVersionDTO) =>
		republish.mutate(
			{ version: version.version, summary: `Roll back to version ${version.version}` },
			{
				onSuccess: (published) => {
					notification.success(
						`Published version ${published.version}: version ${version.version}'s mappings, the bundles are re-mapping`,
					)
					close()
				},
				onError: (cause) => {
					const message = apiErrorMessage(cause, 'Could not re-publish that version.')
					notification.error(message)
					setFailure(message)
				},
			},
		)

	return (
		<CardShell>
			<VersionsTable
				versions={query.data ?? []}
				nameOf={nameOf}
				isLoading={query.isLoading}
				isError={query.isError}
				onRetry={() => void query.refetch()}
				busy={republish.isPending}
				onRepublish={setRollingBackTo}
			/>
			<ConfirmDialog
				open={rollingBackTo !== null}
				title={`Roll back to version ${rollingBackTo?.version ?? ''}?`}
				description="Its mappings are published as a new version, and every bundle is mapped again with them. The current mappings stay in History."
				confirmLabel="Roll back"
				busyLabel="Rolling back…"
				submitting={republish.isPending}
				errorMessage={failure}
				onConfirm={() => rollingBackTo && rollBack(rollingBackTo)}
				onCancel={close}
			/>
		</CardShell>
	)
}
